import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Link2, Loader2, Copy, Check, ExternalLink } from 'lucide-react'
import { templateShareLinkService } from '@/lib/api/services/templateShareLinks'
import type { PermissionLevel, TemplateShareLink } from '@/lib/api/types'
import { PERMISSION_LEVELS } from '@/lib/api/types'
import { useToast } from '@/components/ui/use-toast'

interface CreateShareLinkDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  templateId: string
  templateName: string
  onSuccess: () => void
}

export function CreateShareLinkDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  onSuccess,
}: CreateShareLinkDialogProps) {
  const [loading, setLoading] = useState(false)
  const [createdLink, setCreatedLink] = useState<TemplateShareLink | null>(null)
  const [copied, setCopied] = useState(false)

  // Form state
  const [name, setName] = useState('')
  const [permissionLevel, setPermissionLevel] = useState<PermissionLevel>('View')
  const [expiresAt, setExpiresAt] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [allowedDomains, setAllowedDomains] = useState('')
  const [requiresAuthentication, setRequiresAuthentication] = useState(false)

  const { toast } = useToast()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    setLoading(true)
    try {
      const link = await templateShareLinkService.createShareLink(templateId, {
        name: name || undefined,
        permissionLevel,
        expiresAt: expiresAt || undefined,
        maxUses: maxUses ? parseInt(maxUses, 10) : undefined,
        allowedDomains: allowedDomains || undefined,
        requiresAuthentication,
      })

      setCreatedLink(link)
      toast({
        title: 'Share link created',
        description: 'Your share link is ready to use',
      })
      onSuccess()
    } catch (error) {
      toast({
        title: 'Error',
        description:
          error instanceof Error
            ? error.message
            : 'Failed to create share link',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleCopyLink = async () => {
    if (!createdLink?.shareUrl) return

    try {
      await navigator.clipboard.writeText(createdLink.shareUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to copy link to clipboard',
        variant: 'destructive',
      })
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    // Reset form after a short delay for animation
    setTimeout(() => {
      setCreatedLink(null)
      setCopied(false)
      setName('')
      setPermissionLevel('View')
      setExpiresAt('')
      setMaxUses('')
      setAllowedDomains('')
      setRequiresAuthentication(false)
    }, 200)
  }

  // Only allow View permission for share links (security best practice)
  const shareLinkPermissionLevels = PERMISSION_LEVELS.filter(
    (p) => p.value === 'View'
  )

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
              <Link2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <DialogTitle>
                {createdLink ? 'Share Link Created' : 'Create Share Link'}
              </DialogTitle>
              <DialogDescription>
                {createdLink
                  ? 'Copy the link below to share with others'
                  : `Create a shareable link for "${templateName}"`}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {createdLink ? (
          // Success state - show created link
          <div className="space-y-4 py-4">
            <div className="p-4 rounded-lg bg-muted">
              <Label className="text-xs text-muted-foreground mb-2 block">
                Share Link
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={createdLink.shareUrl || ''}
                  className="font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  className="flex-shrink-0"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              {createdLink.name && (
                <div>
                  <span className="text-muted-foreground">Name:</span>{' '}
                  <span className="font-medium">{createdLink.name}</span>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Permission:</span>{' '}
                <span className="font-medium">View Only</span>
              </div>
              {createdLink.expiresAt && (
                <div>
                  <span className="text-muted-foreground">Expires:</span>{' '}
                  <span className="font-medium">
                    {new Date(createdLink.expiresAt).toLocaleDateString()}
                  </span>
                </div>
              )}
              {createdLink.maxUses && (
                <div>
                  <span className="text-muted-foreground">Max Uses:</span>{' '}
                  <span className="font-medium">{createdLink.maxUses}</span>
                </div>
              )}
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.open(createdLink.shareUrl, '_blank')}
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Link
              </Button>
              <Button type="button" onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </div>
        ) : (
          // Form state - create new link
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            {/* Link Name */}
            <div className="space-y-2">
              <Label>Link Name (Optional)</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., External Review Link"
              />
              <p className="text-xs text-muted-foreground">
                Give this link a name to help identify its purpose
              </p>
            </div>

            {/* Permission Level - Fixed to View for security */}
            <div className="space-y-2">
              <Label>Permission Level</Label>
              <Select
                value={permissionLevel}
                onValueChange={(v) => setPermissionLevel(v as PermissionLevel)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {shareLinkPermissionLevels.map((level) => (
                    <SelectItem key={level.value} value={level.value}>
                      {level.label} - {level.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Share links are view-only for security
              </p>
            </div>

            {/* Expiration */}
            <div className="space-y-2">
              <Label>Expiration Date (Optional)</Label>
              <Input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
              <p className="text-xs text-muted-foreground">
                Leave empty for no expiration
              </p>
            </div>

            {/* Max Uses */}
            <div className="space-y-2">
              <Label>Maximum Uses (Optional)</Label>
              <Input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="Unlimited"
              />
              <p className="text-xs text-muted-foreground">
                Limit how many times this link can be used
              </p>
            </div>

            {/* Allowed Domains */}
            <div className="space-y-2">
              <Label>Allowed Email Domains (Optional)</Label>
              <Input
                value={allowedDomains}
                onChange={(e) => setAllowedDomains(e.target.value)}
                placeholder="example.com, acme.org"
              />
              <p className="text-xs text-muted-foreground">
                Comma-separated list of domains that can access this link
              </p>
            </div>

            {/* Requires Authentication */}
            <div className="flex items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <Label className="text-base">Require Authentication</Label>
                <p className="text-xs text-muted-foreground">
                  Users must be logged in to access this link
                </p>
              </div>
              <Switch
                checked={requiresAuthentication}
                onCheckedChange={setRequiresAuthentication}
              />
            </div>

            <DialogFooter className="pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Link
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
